import React, { useState, useEffect, useRef, lazy } from 'react';
import './ManageUser.css';
import apiClient from '../utils/apiClient';
import * as XLSX from "xlsx/xlsx.mjs";

const ManageUser = () => {
  // AbortController ref
  const abortControllerRef = useRef(null);
  
  // 요청 상태 추적을 위한 ref들
  const isSearchingRef = useRef(false);
  const isLoadingServiceTypesRef = useRef(false);
  const isLoadingContentsRef = useRef(false);
  
  const [formData, setFormData] = useState({
    type: '',
    serviceUrl: '',
    companyName: '',
    name: '',
    contact: '',
    paymentDate: '',
    expiryDate: '',
    manager: '',
    memo: '',
    serialNum: ''
  });

  // 폼 검증 상태
  const [errors, setErrors] = useState({});
  
  // 로딩 상태
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // 검색 관련 상태
  const [searchForm, setSearchForm] = useState({
    type: 'all',
    searchField: 'company', // 'company' 또는 'name'
    searchValue: ''
  });
  const [isSearching, setIsSearching] = useState(false);

  // 모달 관련 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRowIdx, setSelectedRowIdx] = useState(null);
  const [selectedCustomerType, setSelectedCustomerType] = useState(null); // 선택된 고객의 타입
  const [modalForm, setModalForm] = useState({
    type: '',
    link: '',
    image: null,
    imagePreview: null
  });

  // 컨텐츠 이미지 추가 모델
  const [addImageModalForm, setAddImageModalForm] = useState({
    idx: 0,
    contentType: '',
    image: null,
    imagePreview: null
  });
  const [isAddImageModalOpen, setIsAddImageModalOpen] = useState(false);
  const [isAddImageUploadSubmitting, setIsAddImageUploadSubmitting] = useState(false);

  const [contentModalType, setContentModalType] = useState('all');
  const [isModalSubmitting, setIsModalSubmitting] = useState(false);

  // 컨텐츠 보기 팝업 관련 상태
  const [isContentPopupOpen, setIsContentPopupOpen] = useState(false);
  const [selectedCustomerIdx, setSelectedCustomerIdx] = useState(null);
  const [customerContents, setCustomerContents] = useState([]);
  const [isLoadingContents, setIsLoadingContents] = useState(false);
  
  // 일괄 업데이트 모달 관련 상태
  const [isBatchUpdateModalOpen, setIsBatchUpdateModalOpen] = useState(false);
  const [isBatchUpdateSubmitting, setIsBatchUpdateSubmitting] = useState(false);
  const [batchUpdateForm, setBatchUpdateForm] = useState({
    excelFile: null
  });

  // 일괄 업데이트 로그 모달 관련 상태
  const [isBatchLogModalOpen, setIsBatchLogModalOpen] = useState(false);
  const [isBatchLogSubmitting, setIsBatchLogSubmitting] = useState(false);
  const [batchLogForm, setBatchLogForm] = useState({
    searchDate: ""
  });

   // 작업현황 다운로드 모달 관련 상태
   const [isWorkStatusDownloadModalOpen, setIsWorkStatusDownloadModalOpen] = useState(false);
   const [isWorkStatusDownloadSubmitting, setIsWorkStatusDownloadSubmitting] = useState(false);
   const [workStatusDownloadForm, setWorkStatusDownloadForm] = useState({
     searchDate: ""
   });

  // 메모 보기 모달 관련 상태
  const [isViewMemoModalOpen, setIsViewMemoModalOpen] = useState(false);
  const [selectViewMemo, setSelectViewMemo] = useState('');

  // 컨텐츠 선택 관련 상태
  const [selectedContentItems, setSelectedContentItems] = useState([]);
  const [selectAllContents, setSelectAllContents] = useState(false);
  
  // 검색 타입 옵션을 동적으로 계산
  const getSearchTypeOptions = () => [
    { value: 'all', label: '전체' },
    ...serviceTypes
  ];

  // 선택된 고객의 타입에 따라 콘텐츠 타입 옵션을 가져오는 함수
  const getContentTypeOptions = (customerType) => {
    const selectedServiceType = serviceTypes.find(type => type.value === customerType);
    return selectedServiceType ? selectedServiceType.menus : [];
  };

  // 검색 필드 옵션
  const searchFieldOptions = [
    { value: 'company', label: '업체명' },
    { value: 'name', label: '이름' },
    { value: 'phone', label: '연락처'}
  ];

  // 선택된 고객 타입에 따른 동적 모달 타입 옵션
  const getModalTypeOptions = () => {
    return getContentTypeOptions(selectedCustomerType);
  };

  // 서비스 타입 옵션 상태
  const [serviceTypes, setServiceTypes] = useState([]);
  const [isLoadingServiceTypes, setIsLoadingServiceTypes] = useState(false);

  // 광고주 목록 데이터
  const [advertisers, setAdvertisers] = useState([]);

  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  // 편집 모드 관련 상태
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});

  // URL 검증 함수
  const isValidUrl = (url) => {
    try {
      const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*\/?$/;
      return urlPattern.test(url);
    } catch {
      return false;
    }
  };

  // 연락처 포맷팅 함수
  const formatPhoneNumber = (value) => {
    // 숫자만 추출
    const numbers = value.replace(/[^\d]/g, '');
    
    // 길이에 따른 포맷팅
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else if (numbers.length <= 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    } else {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  // 폼 검증 함수
  const validateForm = () => {
    const newErrors = {};

    // 필수 필드 검증
    if (!formData.type) newErrors.type = '서비스 타입을 선택해주세요.';
    if (!formData.serviceUrl) {
      newErrors.serviceUrl = '서비스 URL을 입력해주세요.';
    } else if (!isValidUrl(formData.serviceUrl)) {
      newErrors.serviceUrl = '올바른 URL 형식을 입력해주세요.';
    }
    if (!formData.companyName) newErrors.companyName = '업체명을 입력해주세요.';
    if (!formData.name) newErrors.name = '이름을 입력해주세요.';
    if (!formData.contact) {
      newErrors.contact = '연락처를 입력해주세요.';
    } else if (formData.contact.replace(/[^\d]/g, '').length < 10) {
      newErrors.contact = '올바른 연락처를 입력해주세요.';
    }
    if (!formData.paymentDate) newErrors.paymentDate = '결제일자를 선택해주세요.';
    if (!formData.expiryDate) newErrors.expiryDate = '만료일자를 선택해주세요.';
    if (!formData.manager) newErrors.manager = '담당자를 입력해주세요.';
    
    // 플레이스번호 검증 (올인원 솔루션일 때만 필수)
    if (formData.type === 'all-in-one' && !formData.serialNum) {
      newErrors.serialNum = '올인원 솔루션의 경우 플레이스번호를 입력해주세요.';
    }

    // 날짜 검증
    if (formData.paymentDate && formData.expiryDate) {
      const paymentDate = new Date(formData.paymentDate);
      const expiryDate = new Date(formData.expiryDate);
      if (expiryDate <= paymentDate) {
        newErrors.expiryDate = '만료일자는 결제일자보다 뒤여야 합니다.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    let processedValue = value;

    // 연락처 필드인 경우 포맷팅 적용
    if (field === 'contact') {
      processedValue = formatPhoneNumber(value);
    }

    // URL 필드의 경우 검증만 수행 (자동 추가 제거)
    // 사용자가 직접 프로토콜을 입력하도록 함

    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // 해당 필드의 에러 제거
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleReset = () => {
    setFormData({
      type: '',
      serviceUrl: '',
      companyName: '',
      name: '',
      contact: '',
      paymentDate: '',
      expiryDate: '',
      manager: '',
      memo: '',
      serialNum: ''
    });
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      alert('입력된 정보를 확인해주세요.');
      return;
    }

    setIsSubmitting(true);

    try {
      // 백엔드로 전송할 데이터 준비
      const customerData = {
        type: formData.type,
        url: formData.serviceUrl,
        company: formData.companyName,
        name: formData.name,
        phone: formData.contact,
        paymentDate: formData.paymentDate,
        endDate: formData.expiryDate,
        contractor: formData.manager,
        memo: formData.memo || '',
        serialNum: formData.serialNum || ''
      };

      // 백엔드 API 호출
      const response = await apiClient.post('/Customer/InsertCustomer', customerData);
      
      alert('광고주가 성공적으로 생성되었습니다.');
      handleSearch();
      
    } catch (error) {
      let errorMessage = '광고주 생성에 실패했습니다.';
      if (error.message) {
        errorMessage += `\n오류: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 검색 관련 핸들러
  const handleSearchChange = (field, value) => {
    setSearchForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      // 검색 키워드 업데이트
      handleSearch();
    }
  };

  const handleSearch = async () => {
    if (isSearchingRef.current) {
      return;
    }

    // 요청 시작 플래그 설정
    isSearchingRef.current = true;

    // 이전 요청이 있으면 취소
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // 새로운 AbortController 생성
    abortControllerRef.current = new AbortController();
    
    setIsSearching(true);
    
    try {
      const searchParams = new URLSearchParams();

      searchParams.append('type', searchForm.type);
      
      searchParams.append('searchType', searchForm.searchField);
      searchParams.append('keyword', searchForm.searchValue.trim());
      
      searchParams.append('limit', itemsPerPage.toString());
      searchParams.append('page', currentPage.toString());

      const queryString = searchParams.toString();
      const url = queryString ? `/Customer/SearchCustomer?${queryString}` : '/Customer/SearchCustomer';
      const response = await apiClient.get(url, {}, abortControllerRef.current.signal);
      
      if (response.success && response.data) {
        const formattedAdvertisers = response.data.map(item => ({
          id: item.idx,
          type: item.type,
          label: getTypeLabel(item.type),
          serviceUrl: item.url,
          companyName: item.company,
          name: item.name,
          contact: item.phone,
          serialNum: item.serialNum || '',
          paymentDate: item.paymentDate ? item.paymentDate.split('T')[0] : '',
          expiryDate: item.endDate ? item.endDate.split('T')[0] : '',
          manager: item.contractor,
          memo: item.memo || '-',
          useAlimtalk: item.useAlimtalk || 'N',
          createdAt: formatDate(item.regDate)
        }));
        
        setAdvertisers(formattedAdvertisers);
        setTotalCount(response.totalCount);
        
      } else {
        alert('검색 결과가 없습니다.');
        setAdvertisers([]);
        setTotalCount(0);
      }
      
    } catch (error) {
      // 요청이 취소된 경우 (AbortError) 오류 메시지 표시하지 않음
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        return;
      }
      
      let errorMessage = '검색에 실패했습니다.';
      if (error.message) {
        errorMessage += `\n오류: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      // 요청 완료 플래그 해제
      isSearchingRef.current = false;
      setIsSearching(false);
    }
  };

  // 타입 값을 레이블로 변환하는 헬퍼 함수
  const getTypeLabel = (typeValue) => {
    // serviceTypes가 아직 로딩 중이거나 비어있는 경우 원본 값 반환
    if (!serviceTypes.length) {
      return typeValue;
    }
    
    const searchOptions = getSearchTypeOptions();
    
    const typeOption = searchOptions.find(option => option.value === typeValue);
    
    
    // 타입 옵션을 찾지 못한 경우 원본 값 반환
    return typeOption ? typeOption.label : typeValue;
  };

  // 날짜 포맷 헬퍼 함수
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).replace(/\. /g, '.').replace(/\./g, '.');
  };

  // 서비스 타입 로드 함수
  const loadServiceTypes = async () => {
    // 이미 로딩 중이면 중복 요청 방지 (useRef로 동기적 체크)
    if (isLoadingServiceTypesRef.current) {
      return;
    }

    // 요청 시작 플래그 설정
    isLoadingServiceTypesRef.current = true;
    setIsLoadingServiceTypes(true);
    
    try {
      const response = await apiClient.get('/Customer/GetReportType');
      
      if (response && response.data) {
        // API 응답 데이터를 컴포넌트에서 사용하는 형태로 변환
        const transformedTypes = response.data.map(item => ({
          value: item.id,
          label: item.name || item.title || item.label,
          menus: item.menus || [],
          group: item.group ? item.group === "basic" ? "기본" : "추가" : "기본"
        }));
        
        setServiceTypes(transformedTypes);
      } else {
        setServiceTypes([]);
      }
    } catch (error) {
      alert(error);
      // 오류 발생 시 사용자에게 알림
      alert('서비스 타입을 불러오는데 실패했습니다.');
      
      // 실패 시 빈 배열로 설정
      setServiceTypes([]);
    } finally {
      // 요청 완료 플래그 해제
      isLoadingServiceTypesRef.current = false;
      setIsLoadingServiceTypes(false);
    }
  };

  // 컴포넌트 마운트 시 서비스 타입 로드 후 검색 수행
  useEffect(() => {
    const load = async () => {
      await loadServiceTypes(); // 여기서 setServiceTypes 호출
    };
    load();
  }, []);
  
  useEffect(() => {
    if (serviceTypes.length > 0) {
      handleSearch(); // state가 갱신된 뒤 실행됨
    }
  }, [serviceTypes]);

  // currentPage나 itemsPerPage가 변경될 때 검색 재실행
  useEffect(() => {
    if (serviceTypes.length > 0) {
      handleSearch();
    }
  }, [currentPage, itemsPerPage]);

  // 컴포넌트 언마운트 시 요청 취소 및 메모리 누수 방지
  useEffect(() => {
    return () => {
      // 진행 중인 API 요청 취소
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // 이미지 미리보기 URL 메모리 해제
      if (modalForm.imagePreview) {
        URL.revokeObjectURL(modalForm.imagePreview);
      }
    };
  }, [modalForm.imagePreview]);

  // 모달 관련 핸들러
  const handleAddClick = (idx) => {
    setSelectedRowIdx(idx);    
    // 선택된 고객의 타입 정보 가져오기
    const selectedCustomer = advertisers.find(advertiser => advertiser.id === idx);

    const customerType = selectedCustomer ? selectedCustomer.type : '';
    setSelectedCustomerType(customerType);
    
    setModalForm({
      type: '',
      link: '',
      image: null,
      imagePreview: null
    });
    setIsModalOpen(true);
  };

  // 이미지 추가 모달 관련 핸들러
  const handleAddImageModalClick = (idx, type, contentType) => {
    if (contentType !== "image") {
      alert("등록이 불가한 컨텐츠 타입입니다.");
      return;
    }
    setAddImageModalForm({
      idx: idx,
      contentType: type,
      image: null,
      imagePreview: null
    });
    setIsAddImageModalOpen(true);
  };

  const handleAddImageModalClose = () => {
    setIsAddImageModalOpen(false);
    setAddImageModalForm({
      idx: 0,
      contentType: '',
      image: null,
      imagePreview: null
    });
  };

  const handleModalClose = () => {
    // 메모리 누수 방지를 위해 미리보기 URL 해제
    if (modalForm.imagePreview) {
      URL.revokeObjectURL(modalForm.imagePreview);
    }
    
    setIsModalOpen(false);
    setSelectedRowIdx(null);
    setModalForm({
      type: '',
      link: '',
      image: null,
      imagePreview: null
    });
  };

  const handleBatchUpdateModalClose = () => {
    setIsBatchUpdateModalOpen(false);
    setBatchUpdateForm({
      excelFile: null
    });
  };

  const handleBatchLogModalClose = () => {
    setIsBatchLogModalOpen(false);
    setBatchLogForm({
      searchDate: ""
    });
  };
  
  const handleWorkStatusDownloadModalClose = () => {
    setIsWorkStatusDownloadModalOpen(false);
    setWorkStatusDownloadForm({
      searchDate: ""
    });
  };

  const handleViewMemoModalClose = () => {
    setIsViewMemoModalOpen(false);
    setSelectViewMemo('');
  };

  const handleModalFormChange = (field, value) => {
    setModalForm(prev => {
      const newForm = {
        ...prev,
        [field]: value
      };
      
      // 타입이 변경되고, 이미지가 필요하지 않은 타입으로 변경된 경우 이미지 초기화
      if (field === 'type' && isImageNotRequired(value)) {
        // 기존 미리보기 URL 메모리 해제
        if (prev.imagePreview) {
          URL.revokeObjectURL(prev.imagePreview);
        }
        newForm.image = null;
        newForm.imagePreview = null;
      }
      
      return newForm;
    });
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // 이전 미리보기 URL 메모리 해제
      if (modalForm.imagePreview) {
        URL.revokeObjectURL(modalForm.imagePreview);
      }
      
      // 새 미리보기 URL 생성
      const previewUrl = URL.createObjectURL(file);
      
      setModalForm(prev => ({
        ...prev,
        image: file,
        imagePreview: previewUrl
      }));
    }
  };

  const handleImageUpload2 = (event) => {
    const file = event.target.files[0];
    if (file) {
      // 이전 미리보기 URL 메모리 해제
      if (addImageModalForm.imagePreview) {
        URL.revokeObjectURL(addImageModalForm.imagePreview);
      }
      
      // 새 미리보기 URL 생성
      const previewUrl = URL.createObjectURL(file);
      
      setAddImageModalForm(prev => ({
        ...prev,
        image: file,
        imagePreview: previewUrl
      }));
    }
  };

  const handleImageUploadSubmit = async () => {
    setIsAddImageUploadSubmitting(true);
    
    try {
      const hasFile = addImageModalForm.image;
      
      if (!hasFile) {
        alert('이미지를 업로드해주세요.');
        return;
      }

      if (hasFile && !validateImageFile(addImageModalForm.image)) {
        alert('올바른 이미지 파일을 선택해주세요. (JPEG, PNG, GIF, WebP 형식만 지원)');
        return;
      }

      const formData = new FormData();
      formData.append('idx', addImageModalForm.idx.toString());
      formData.append('contentType', addImageModalForm.contentType);

      if (addImageModalForm.image) {
        formData.append('fileData', addImageModalForm.image);
      }

      const response = await apiClient.uploadRequestWithTimeout('/Customer/AddCustomerContentImage', formData, 600000);
      
      if (response && response.success) {
        await handleViewInnerContents(selectedCustomerIdx, addImageModalForm.contentType);
      
        alert(response.message);
        handleAddImageModalClose();
      }
      else {
        alert(response.message);
      }
      
      
    } catch (error) {
      console.error('이미지 추가 실패:', error);
      
      let errorMessage = '이미지 추가에 실패했습니다.';
      if (error.message) {
        errorMessage += `\n오류: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setIsAddImageUploadSubmitting(false);
    }
  };

  // 이미지 파일 검증 함수
  const validateImageFile = (file) => {
    if (!file) return true; // 파일이 없으면 검증 통과
    
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return false;
    }
    
    // 파일 크기 검증 (예: 5MB 제한)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('이미지 파일 크기는 5MB 이하여야 합니다.');
      return false;
    }
    
    return true;
  };

  // 이미지가 필요하지 않은 타입인지 확인하는 헬퍼 함수
  const isImageNotRequired = (type) => {
    return type === 'blog-review' || type === 'promotion-video' || type === 'reels-video';
  };

  const handleModalSubmit = async () => {
    setIsModalSubmitting(true);
    
    try {
      if (!modalForm.type.trim()) {
        alert('타입을 선택해주세요.');
        return;
      }

      const hasUrl = modalForm.link.trim();
      
      if (!hasUrl) {
        alert('URL을 입력해주세요.');
        return;
      }
      
      const imageRequired = !isImageNotRequired(modalForm.type);
      const hasFile = modalForm.image;
      
      if (imageRequired && !hasFile) {
        alert('이미지를 업로드해주세요.');
        return;
      }

      if (hasFile && !validateImageFile(modalForm.image)) {
        alert('올바른 이미지 파일을 선택해주세요. (JPEG, PNG, GIF, WebP 형식만 지원)');
        return;
      }

      const formData = new FormData();
      formData.append('customerIdx', selectedRowIdx.toString());
      formData.append('type', modalForm.type);
      formData.append('url', modalForm.link.trim());
      
      if (!isImageNotRequired(modalForm.type) && modalForm.image) {
        formData.append('fileData', modalForm.image);
      }

      const response = await apiClient.upload('/Customer/InsertCustomerContent', formData);
      
      alert('콘텐츠가 성공적으로 추가되었습니다.');
      handleModalClose();
      
    } catch (error) {
      let errorMessage = '콘텐츠 추가에 실패했습니다.';
      if (error.message) {
        errorMessage += `\n오류: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setIsModalSubmitting(false);
    }
  };

  const handleBatchUpdateModalOpen = () => {
    setBatchUpdateForm({
      excelFile: null
    });
    setIsBatchUpdateSubmitting(false);
    setIsBatchUpdateModalOpen(true);
  };

  const handleBatchLogModalOpen = () => {
    setBatchLogForm({
      searchDate: ""
    });
    setIsBatchLogSubmitting(false);
    setIsBatchLogModalOpen(true);
  };

  const handleWorkStatusDownloadModalOpen = () => {
    setWorkStatusDownloadForm({
      searchDate: ""
    });
    setIsWorkStatusDownloadSubmitting(false);
    setIsWorkStatusDownloadModalOpen(true);
  };

  const handleViewMemoModalOpen = (memo) => {
    if (memo === "" || memo === undefined || memo === "-") return;
    setSelectViewMemo(memo);
    setIsViewMemoModalOpen(true);
  };

  const handleSendAlimtalk = async () => {
    if(selectedItems.length == 0){
      alert('선택된 항목이 없습니다.');
      return;
    }
    if(!window.confirm('선택된 항목에 대해 알림톡을 발송하시겠습니까?')){
        return;
      }
    try{
      const selectedIdxList = selectedItems.map(idx => {
        return {
          customerIdx: idx,
          templateCode: "menu",
        };
      });

      const response = await apiClient.post('/Alimtalk/SendMultiMessage', selectedIdxList);
      if(response && response.success){
        alert(response.message);
      }else{
        alert(response.message);
      }
    }
    catch(error){
      console.error('알림톡 발송 실패:', error);
      alert(error.message);
    }
  };
  const handleSendContentAlimtalk = async () => {
    if (contentModalType == "all"){
      alert('전체 컨텐츠에 대해 알림톡을 발송할 수 없습니다.');
      return;
    }
    const transContentType = getModalTypeOptions().find(type => type.id === contentModalType)?.name;
    if(!window.confirm(transContentType + '에 대해 알림톡을 발송하시겠습니까?')){
        return;
      }
    try{
      const sendData = {
        customerIdx: selectedCustomerIdx,
        templateCode: "content",
        contentType: contentModalType,
      };

      const response = await apiClient.post('/Alimtalk/SendMessage', sendData);
      if(response && response.success){
        alert('알림톡 발송이 성공적으로 완료되었습니다.');
      }else{
        alert(response.message);
      }
    }
    catch(error){
      console.error('알림톡 발송 실패:', error);
      alert(error.message);
    }
  };

  const handleBatchUpdateFormChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setBatchUpdateForm(prev => ({
        ...prev,
        excelFile: file,
      }));
    }else{
      setBatchUpdateForm(prev => ({
        ...prev,
        excelFile: null,
      }));
    }
  };
  const handleBatchUpdateSubmit = async () => {
    setIsBatchUpdateSubmitting(true);
    
    try {
      // 5. FormData 준비 (파일 업로드를 위해)
      const formData = new FormData();
      formData.append('data', batchUpdateForm.excelFile);

      // 5. 백엔드 API 호출
      const response = await apiClient.uploadRequestWithTimeout('/Customer/UploadExcelContentDB', formData, 600000);
      
      if (response && response.success) {
        alert(response.message);
        handleBatchUpdateModalClose();
      }
      else {
        alert(response.message);
      }
      
      
    } catch (error) {
      console.error('콘텐츠 추가 실패:', error);
      
      let errorMessage = '콘텐츠 추가에 실패했습니다.';
      if (error.message) {
        errorMessage += `\n오류: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setIsBatchUpdateSubmitting(false);
    }
  };

  const handleUserUniqeSubmit = async () => {
    setIsBatchLogSubmitting(true);
    
    try {
      const response = await apiClient.download('/Customer/UserUniqueDownload', {}, null, "고객 고유번호.xlsx");
      //handleBatchLogModalClose();
    } catch (error) {
      console.error('다운로드 실패:', error);
      
      let errorMessage = '다운로드에 실패했습니다.';
      if (error.message) {
        errorMessage += `\n오류: ${error.message}`;
      }
      
      alert(errorMessage);
    }
  };

  const handleBatchLogSubmit = async () => {
    setIsBatchLogSubmitting(true);
    
    try {
      const response = await apiClient.download('/Customer/ExcelLogDownload?date=' + batchLogForm.searchDate, {}, null, "일괄업데이트로그.xlsx");
      handleBatchLogModalClose();
    } catch (error) {
      console.error('다운로드 실패:', error);
      
      let errorMessage = '다운로드에 실패했습니다.';
      if (error.message) {
        errorMessage += `\n오류: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setIsBatchLogSubmitting(false);
    }
  };

  const handleWorkStatusDownloadSubmit = async () => {
    setIsWorkStatusDownloadSubmitting(true);
    
    try {
      const response = await apiClient.download('/Customer/UploadLogDownload?date=' + workStatusDownloadForm.searchDate, {}, null, "작업현황.xlsx");
      handleWorkStatusDownloadModalClose();
    } catch (error) {
      console.error('다운로드 실패:', error);
      
      let errorMessage = '다운로드에 실패했습니다.';
      if (error.message) {
        errorMessage += `\n오류: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setIsWorkStatusDownloadSubmitting(false);
    }
  };

  const handleCopyLink = (idx) => {
    const link = `https://h-ad-report.com/marketingInfo/${idx}`;
    navigator.clipboard.writeText(link);
    alert('링크가 복사되었습니다.');
  };
  // 컨텐츠 보기 관련 핸들러
  const handleViewContents = async (customerIdx) => {
    if (isLoadingContentsRef.current) {
      return;
    }

    // 요청 시작 플래그 설정
    isLoadingContentsRef.current = true;
    setSelectedCustomerIdx(customerIdx);
    setIsContentPopupOpen(true);
    setIsLoadingContents(true);
    const selectedCustomer = advertisers.find(advertiser => advertiser.id === customerIdx);

    const customerType = selectedCustomer ? selectedCustomer.type : '';
    setSelectedCustomerType(customerType);
    try {
      const response = await apiClient.get(`/Customer/GetCustomerContentList?idx=${customerIdx}&type=all`);
      
      if (response && response.data && response.data.length > 0) {
        setCustomerContents(response.data);
      } else {
        setCustomerContents([]);
      }
      
    } catch (error) {
      setCustomerContents([]);
      
      // AbortError가 아닌 경우에만 알림 표시
      if (error.name !== 'AbortError' && !error.message?.includes('aborted')) {
        alert('컨텐츠 조회에 실패했습니다.');
      }
    } finally {
      // 요청 완료 플래그 해제
      isLoadingContentsRef.current = false;
      setIsLoadingContents(false);
    }
  };

  // 컨텐츠 내부 조회 기능
  const handleViewInnerContents = async (customerIdx, type) => {
    try {
      setContentModalType(type);
      // 백엔드 API 호출
      const response = await apiClient.get(`/Customer/GetCustomerContentList?idx=${customerIdx}&type=${type}`);
      
      if (response && response.data && response.data.length > 0) {
        setCustomerContents(response.data);
      } else {
        setCustomerContents([]);
      }
      
    } catch (error) {
      setCustomerContents([]);
      
      // AbortError가 아닌 경우에만 알림 표시
      if (error.name !== 'AbortError' && !error.message?.includes('aborted')) {
        alert('컨텐츠 조회에 실패했습니다.');
      }
    } finally {
      setIsLoadingContents(false);
    }
  };

  const handleCloseContentPopup = () => {
    setIsContentPopupOpen(false);
    setSelectedCustomerIdx(null);
    setCustomerContents([]);
    setSelectedContentItems([]);
    setSelectAllContents(false);
  };

  // 컨텐츠 체크박스 관련 핸들러
  const handleSelectAllContents = (checked) => {
    setSelectAllContents(checked);
    if (checked) {
      setSelectedContentItems(customerContents.map((_, index) => index));
    } else {
      setSelectedContentItems([]);
    }
  };

  const handleSelectContentItem = (index, checked) => {
    if (checked) {
      const newSelection = [...selectedContentItems, index];
      setSelectedContentItems(newSelection);
      
      // 모든 항목이 선택되었는지 확인
      if (newSelection.length === customerContents.length) {
        setSelectAllContents(true);
      }
    } else {
      setSelectedContentItems(prev => prev.filter(i => i !== index));
      setSelectAllContents(false);
    }
  };

  // 개별 컨텐츠 삭제
  const handleDeleteContent = async (contentIndex) => {
    if (!window.confirm('이 컨텐츠를 삭제하시겠습니까?')) {
      return;
    }

    try {
      const content = customerContents[contentIndex];
      
      if (!content.idx) {
        alert('삭제할 컨텐츠의 ID를 찾을 수 없습니다.');
        return;
      }

      const response =await apiClient.post('/Customer/DeleteCustomerContent', [content.idx]);
      
      if (response && response.success) {
        // 삭제 완료 후 재조회
        await handleViewInnerContents(selectedCustomerIdx, contentModalType);
      
        // 선택 상태 초기화
        setSelectedContentItems([]);
        setSelectAllContents(false);
        alert('컨텐츠가 삭제되었습니다.');
      } else {
        alert(response.message);
      }
      
      
      
    } catch (error) {
      console.error('컨텐츠 삭제 실패:', error);
      
      let errorMessage = '컨텐츠 삭제에 실패했습니다.';
      if (error.message) {
        errorMessage += `\n오류: ${error.message}`;
      }
      
      alert(errorMessage);
    }
  };


  const handleExcelDownload = async () => {
    let fileName = "컨텐츠 목록";
    const datas = customerContents?.length ? customerContents : []; // data가 없으면 빈 배열
    if (datas.length === 0){
      alert('다운로드 할 컨텐츠가 없습니다.');
      return;
    }

    const contentTypes = getModalTypeOptions();
    const selectedCustomer = advertisers.find(advertiser => advertiser.id === selectedCustomerIdx);
    if (contentModalType == "all"){
      fileName = selectedCustomer.companyName + "_전체컨텐츠목록_" + formatDate(new Date());
    }else{
      fileName = selectedCustomer.companyName + "_" + contentTypes.find(type => type.id === contentModalType)?.name + "_컨텐츠목록_" + formatDate(new Date());
    }
    const excelData = datas.map(el => {
      return {
        "업체명": selectedCustomer.companyName,
        "타입": contentTypes.find(type => type.id === el.type)?.name || el.type,
        "URL": el.url,
        "등록일": formatDate(el.regDate),
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(excelData); // JSON 데이터를 시트로 변환
    const workbook = XLSX.utils.book_new(); // 엑셀 파일 생성하기
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1"); // 생성된 엑셀 파일에 'Sheet1' 시트 추가
    XLSX.writeFile(workbook, fileName ? `${fileName}.xlsx` : "data.xlsx");
  };

  // 선택된 컨텐츠들 삭제
  const handleDeleteSelectedContents = async () => {
    if (selectedContentItems.length === 0) {
      alert('삭제할 컨텐츠를 선택해주세요.');
      return;
    }

    if (!window.confirm(`선택된 ${selectedContentItems.length}개의 컨텐츠를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      // 선택된 인덱스에 해당하는 컨텐츠의 idx 수집
      const selectedIdxList = selectedContentItems.map(index => {
        const content = customerContents[index];
        if (!content.idx) {
          throw new Error(`인덱스 ${index}의 컨텐츠에서 ID를 찾을 수 없습니다.`);
        }
        return content.idx;
      });

      var response = await apiClient.post('/Customer/DeleteCustomerContent', selectedIdxList);
      
      if (response && response.success) {
        alert(`${selectedContentItems.length}개의 컨텐츠가 삭제되었습니다.`);
      
        // 삭제 완료 후 재조회
        await handleViewInnerContents(selectedCustomerIdx, contentModalType);
        
        // 선택 상태 초기화
        setSelectedContentItems([]);
        setSelectAllContents(false);
      } else {
        alert(response.message);
      }

      
      
    } catch (error) {
      let errorMessage = '컨텐츠 삭제에 실패했습니다.';
      if (error.message) {
        errorMessage += `\n오류: ${error.message}`;
      }
      
      alert(errorMessage);
    }
  };

  // 체크박스 관련 핸들러
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedItems(advertisers.map(item => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id, checked) => {
    if (checked) {
      setSelectedItems(prev => [...prev, id]);
    } else {
      setSelectedItems(prev => prev.filter(item => item !== id));
      setSelectAll(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) {
      alert('삭제할 광고주를 선택해주세요.');
      return;
    }
    if (selectedItems.length > 0) {
      alert(`${selectedItems.length}개 항목을 삭제하시겠습니까?`);
    }

    try {
      // 백엔드 API 호출
      var response = await apiClient.post('/Customer/DeleteCustomer', selectedItems);
      
      if (response && response.success) {
        //alert(`${selectedItems.length}개의 광고주가 삭제되었습니다.`);
        alert('계약기간이 만료되었습니다.\n서비스를 이용해주셔서 감사합니다.');

        // 삭제 완료 후 재조회
        await handleSearch();
        
        // 선택 상태 초기화
        setSelectedItems([]);
        setSelectAll(false);
      }
      else {
        alert(response.message);
      }
      
    } catch (error) {
      let errorMessage = '광고주 삭제에 실패했습니다.';
      if (error.message) {
        errorMessage += `\n오류: ${error.message}`;
      }
      
      alert(errorMessage);
    }

  };

  // 편집 관련 핸들러
  const handleEdit = async (id) => {
    if (editingId === id) {
      // 수정 완료
      editingData.idx = id;
      editingData.type = editingData.type;
      editingData.company = editingData.companyName;
      editingData.phone = editingData.contact;
      editingData.contractor = editingData.manager;
      editingData.endDate = editingData.expiryDate;
      editingData.memo = editingData.memo;
      await apiClient.post('/Customer/UpdateCustomer', editingData);
      await handleSearch();
      
      setEditingId(null);
      setEditingData({});
      alert('수정이 완료되었습니다.');
    } else {
      // 편집 시작
      const advertiser = advertisers.find(a => a.id === id);
      setEditingId(id);
      setEditingData({
        type: advertiser.type,
        serviceUrl: advertiser.serviceUrl,
        companyName: advertiser.companyName,
        name: advertiser.name,
        contact: advertiser.contact,
        serialNum: advertiser.serialNum,
        paymentDate: advertiser.paymentDate,
        expiryDate: advertiser.expiryDate,
        manager: advertiser.manager,
        memo: advertiser.memo
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData({});
  };

  const handleEditingDataChange = (field, value) => {
    let processedValue = value;

    // 연락처 필드인 경우 포맷팅 적용
    if (field === 'contact') {
      processedValue = formatPhoneNumber(value);
    }

    setEditingData(prev => ({
      ...prev,
      [field]: processedValue
    }));
  };

  const handleDelete = async (id) => {
    if (editingId === id) {
      alert('편집 중인 항목은 삭제할 수 없습니다.');
      return;
    }
    
    if (window.confirm('이 항목을 삭제하시겠습니까?')) {
      
      const response = await apiClient.post('/Customer/DeleteCustomer', [id]);
      if (response && response.success) {
        alert('계약기간이 만료되었습니다.\n서비스를 이용해주셔서 감사합니다.');
        await handleSearch();
      }
      else {
        alert(response.message);
      }
    }
  };

  const handleUseAlimtalk = async (id) => {
    if (editingId === id) {
      alert('편집 중인 항목은 설정을 변경할 수 없습니다.');
      return;
    }
    
    if (window.confirm('해당 사용자의 알림톡 설정을 변경하시겠습니까?')) {
      
      const response = await apiClient.post('/Customer/ConvertCustomerAlimType', [id]);
      if (response && response.success) {
        alert('알림톡 설정이 변경되었습니다.');
        await handleSearch();
      }
      else {
        alert(response.message);
      }
    }
  };

  // 페이지네이션
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const currentItems = advertisers;

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 10;
    
    // 현재 페이지를 기준으로 표시할 페이지 범위 계산
    const halfVisible = Math.floor(maxVisiblePages / 2);
    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // 끝 페이지가 최대 표시 개수에 못 미치는 경우 시작 페이지 조정
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    // 이전 버튼
    pages.push(
      <button
        key="prev"
        className="pagination-btn"
        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
        disabled={currentPage === 1}
      >
        &lt;
      </button>
    );

    // 페이지 번호들 (연속적으로 표시)
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </button>
      );
    }

    // 다음 버튼
    pages.push(
      <button
        key="next"
        className="pagination-btn"
        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
        disabled={currentPage === totalPages}
      >
        &gt;
      </button>
    );

    return pages;
  };

  // 서비스 타입 로딩 중일 때는 로딩 UI 표시
  if (isLoadingServiceTypes) {
    return (
      <main className="manage-user">
        <div className="page-header">
          <h1>고객 생성</h1>
          <p>결제 고객 생성</p>
        </div>
        <div className="service-type-loading-container">
          <div className="service-type-loading-spinner"></div>
          <p className="service-type-loading-text">
            서비스 타입을 불러오는 중...
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="manage-user">
      <div className="page-header">
        <h1>고객 생성</h1>
        <p>결제 고객 생성</p>
      </div>

      <div className="content-section">
        <div className="section-header">
          <h2>광고주 생성</h2>
          <p>새로운 광고주를 생성하세요</p>
        </div>

        <form onSubmit={handleSubmit} className="user-form">
          {/* 첫 번째 행: 타입, 플레이스번호 */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">타입 *</label>
              <select
                id="type"
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                required
                className={errors.type ? 'error' : ''}
                disabled={isLoadingServiceTypes}
              >
{Object.entries(
    serviceTypes.reduce((acc, type) => {
      if (!acc[type.group]) acc[type.group] = [];
      acc[type.group].push(type);
      return acc;
    }, {})
  ).map(([groupName, types]) => (
    <optgroup key={groupName} label={groupName}>
      {types.map(type => (
        <option key={type.value} value={type.value}>
          {type.label}
        </option>
      ))}
    </optgroup>
  ))}

                {/* <option value="" disabled hidden>
                  {isLoadingServiceTypes ? '타입을 불러오는 중...' : '타입을 선택하세요'}
                </option>
                {serviceTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))} */}
              </select>
              {errors.type && <span className="error-text">{errors.type}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="serialNum">
                플레이스번호 {formData.type === 'all-in-one' ? '*' : ''}
              </label>
              <input
                type="text"
                id="serialNum"
                placeholder="플레이스번호를 입력하세요"
                value={formData.serialNum}
                onChange={(e) => handleChange('serialNum', e.target.value)}
                required={formData.type === 'all-in-one'}
                className={errors.serialNum ? 'error' : ''}
              />
              {errors.serialNum && <span className="error-text">{errors.serialNum}</span>}
              <span className="field-hint">
                {formData.type === 'all-in-one' 
                  ? '올인원 솔루션의 경우 필수입니다.' 
                  : '선택 입력사항입니다.'}
              </span>
            </div>
          </div>

          {/* 두 번째 행: 서비스 URL (전체 너비) */}
          <div className="form-group full-width">
            <label htmlFor="serviceUrl">서비스 URL *</label>
            <input
              type="url"
              id="serviceUrl"
              placeholder="https://example.com"
              value={formData.serviceUrl}
              onChange={(e) => handleChange('serviceUrl', e.target.value)}
              required
              className={errors.serviceUrl ? 'error' : ''}
            />
            {errors.serviceUrl && <span className="error-text">{errors.serviceUrl}</span>}
          </div>

          {/* 세 번째 행: 업체명, 이름 */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="companyName">업체명 *</label>
              <input
                type="text"
                id="companyName"
                placeholder="업체명을 입력하세요"
                value={formData.companyName}
                onChange={(e) => handleChange('companyName', e.target.value)}
                required
                className={errors.companyName ? 'error' : ''}
              />
              {errors.companyName && <span className="error-text">{errors.companyName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="name">이름 *</label>
              <input
                type="text"
                id="name"
                placeholder="이름을 입력하세요"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>
          </div>

          {/* 네 번째 행: 연락처, 담당자 */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="contact">연락처 *</label>
              <input
                type="tel"
                id="contact"
                placeholder="010-1234-5678"
                value={formData.contact}
                onChange={(e) => handleChange('contact', e.target.value)}
                required
                className={errors.contact ? 'error' : ''}
                maxLength="13"
              />
              {errors.contact && <span className="error-text">{errors.contact}</span>}
              <span className="field-hint">숫자만 입력하면 자동으로 휴대폰 형식으로 변환됩니다.</span>
            </div>

            <div className="form-group">
              <label htmlFor="manager">담당자 *</label>
              <input
                type="text"
                id="manager"
                placeholder="담당자명을 입력하세요"
                value={formData.manager}
                onChange={(e) => handleChange('manager', e.target.value)}
                required
                className={errors.manager ? 'error' : ''}
              />
              {errors.manager && <span className="error-text">{errors.manager}</span>}
            </div>
          </div>

          {/* 다섯 번째 행: 결제일자, 만료일자 */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="paymentDate">결제일자 *</label>
              <input
                type="date"
                id="paymentDate"
                value={formData.paymentDate}
                onChange={(e) => handleChange('paymentDate', e.target.value)}
                required
                className={errors.paymentDate ? 'error' : ''}
              />
              {errors.paymentDate && <span className="error-text">{errors.paymentDate}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="expiryDate">만료일자 *</label>
              <input
                type="date"
                id="expiryDate"
                value={formData.expiryDate}
                onChange={(e) => handleChange('expiryDate', e.target.value)}
                required
                className={errors.expiryDate ? 'error' : ''}
              />
              {errors.expiryDate && <span className="error-text">{errors.expiryDate}</span>}
            </div>
          </div>

          {/* 메모 (전체 너비) */}
          <div className="form-group full-width">
            <label htmlFor="memo">메모</label>
            <textarea
              id="memo"
              placeholder="메모를 입력하세요"
              value={formData.memo}
              onChange={(e) => handleChange('memo', e.target.value)}
              rows="6"
              className={errors.memo ? 'error' : ''}
            />
            {errors.memo && <span className="error-text">{errors.memo}</span>}
          </div>

          <div className="form-actions">
            <button type="button" className="reset-btn" onClick={handleReset}>
              초기화
            </button>
            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? '생성 중...' : '광고주 생성'}
            </button>
          </div>
        </form>
      </div>

      {/* 광고주 목록 섹션 */}
      <div className="content-section list-section">
        <div className="section-header">
          <h2>광고주 목록</h2>
          <p>생성된 광고주 목록을 확인하세요</p>
        </div>

        {/* 검색 폼 */}
        <div className="search-section">
          <div className="search-form">
            <div className="search-row">
              <div className="search-field">
                <label htmlFor="searchType">타입</label>
                <select
                  id="searchType"
                  value={searchForm.type}
                  onChange={(e) => handleSearchChange('type', e.target.value)}
                  className="search-select"
                >
                  {getSearchTypeOptions().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="search-field">
                <label htmlFor="searchField">검색 필드</label>
                <select
                  id="searchField"
                  value={searchForm.searchField}
                  onChange={(e) => handleSearchChange('searchField', e.target.value)}
                  className="search-select"
                >
                  {searchFieldOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="search-field">
                <label htmlFor="searchValue">검색어</label>
                <input
                  type="text"
                  id="searchValue"
                  value={searchForm.searchValue}
                  onChange={(e) => handleSearchChange('searchValue', e.target.value)}
                  placeholder={`${searchForm.searchField === 'company' ? '업체명' : searchForm.searchField === 'name' ? '이름' : '연락처 뒷자리'}을 입력하세요`}
                  className="search-input"
                  onKeyDown={handleSearchKeyPress}
                />
              </div>
              
              <div className="search-actions">
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={isSearching}
                  className="search-btn"
                >
                  {isSearching ? '검색 중...' : '검색'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 필터 및 컨트롤 */}
        <div className="list-controls">
          <div className="control-left">
            <span className="selected-count">{selectedItems.length}개 선택됨</span>
            <button 
              className="delete-selected-btn"
              onClick={handleDeleteSelected}
              disabled={selectedItems.length === 0}
            >
              선택 삭제
            </button>
            <button 
              className="batch-update-btn"
              onClick={handleBatchUpdateModalOpen}
            >
              일괄 업데이트
            </button>
            <button 
              className="send-alimtalk-btn"
              onClick={handleSendAlimtalk}
            >
              선택 알림톡 발송
            </button>
            <button 
              className="excel-download-btn"
              onClick={handleUserUniqeSubmit}
            >
              유저 고유번호 다운로드
            </button>
            <button 
              className="excel-download-btn"
              onClick={handleBatchLogModalOpen}
            >
              일괄 업데이트 로그 다운로드
            </button>
            <button 
              className="excel-download-btn"
              onClick={handleWorkStatusDownloadModalOpen}
            >
              작업현황 다운로드
            </button>
          </div>
          <div className="control-right">
            <select 
              value={itemsPerPage} 
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="items-per-page-select"
            >
              <option value={10}>10개 보기</option>
              <option value={20}>20개 보기</option>
              <option value={50}>50개 보기</option>
            </select>
          </div>
        </div>

        {/* 테이블 */}
        <div className="table-container">
          <table className="advertisers-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th>No.</th>
                <th>링크</th>
                <th>타입</th>
                <th>업체명</th>
                <th>이름</th>
                <th>연락처</th>
                <th>플레이스번호</th>
                <th>결제일</th>
                <th>만료일</th>
                <th>담당자</th>
                <th>메모</th>
                <th>생성일</th>
                <th>알림톡</th>
                <th>추가</th>
                <th>보기</th>
                <th>편집</th>
                <th>삭제</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((advertiser, index) => (
                <tr key={advertiser.id} className={editingId === advertiser.id ? 'editing' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(advertiser.id)}
                      onChange={(e) => handleSelectItem(advertiser.id, e.target.checked)}
                      disabled={editingId === advertiser.id}
                    />
                  </td>
                  <td>{advertiser.id}</td>
                  <td>
                    <button 
                      className="action-btn copy-btn"
                      onClick={() => handleCopyLink(advertiser.id)}
                      title="링크"
                      disabled={editingId === advertiser.id}
                    >
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
  <g fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="10" height="12" rx="2"/>
    <path d="M7 15H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1"/>
  </g>
</svg>

                    </button>
                  </td>
                  <td className="type-col">
                    {editingId === advertiser.id ? (
                      <select
                        value={editingData.type || ''}
                        onChange={(e) => handleEditingDataChange('type', e.target.value)}
                        className="edit-select"
                      >
                        {serviceTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      advertiser.label
                    )}
                  </td>
                  <td className="company-name">
                    {editingId === advertiser.id ? (
                      <input
                        type="text"
                        value={editingData.companyName || ''}
                        onChange={(e) => handleEditingDataChange('companyName', e.target.value)}
                        className="edit-input"
                      />
                    ) : (
                      advertiser.companyName
                    )}
                  </td>
                  <td>
                    {editingId === advertiser.id ? (
                      <input
                        type="text"
                        value={editingData.name || ''}
                        onChange={(e) => handleEditingDataChange('name', e.target.value)}
                        className="edit-input"
                      />
                    ) : (
                      advertiser.name
                    )}
                  </td>
                  <td className="contact-col">
                    {editingId === advertiser.id ? (
                      <input
                        type="tel"
                        value={editingData.contact || ''}
                        onChange={(e) => handleEditingDataChange('contact', e.target.value)}
                        className="edit-input"
                        placeholder="010-1234-5678"
                        maxLength="13"
                      />
                    ) : (
                      advertiser.contact
                    )}
                  </td>
                  <td className="serial-num-col">
                    {editingId === advertiser.id ? (
                      <input
                        type="text"
                        value={editingData.serialNum || ''}
                        onChange={(e) => handleEditingDataChange('serialNum', e.target.value)}
                        className="edit-input"
                        placeholder="플레이스번호"
                      />
                    ) : (
                      advertiser.serialNum || '-'
                    )}
                  </td>
                  <td className="date-col">
                    {editingId === advertiser.id ? (
                      <input
                        type="date"
                        value={editingData.paymentDate || ''}
                        onChange={(e) => handleEditingDataChange('paymentDate', e.target.value)}
                        className="edit-input"
                      />
                    ) : (
                      advertiser.paymentDate
                    )}
                  </td>
                  <td className="date-col">
                    {editingId === advertiser.id ? (
                      <input
                        type="date"
                        value={editingData.expiryDate || ''}
                        onChange={(e) => handleEditingDataChange('expiryDate', e.target.value)}
                        className="edit-input"
                      />
                    ) : (
                      advertiser.expiryDate
                    )}
                  </td>
                  <td>
                    {editingId === advertiser.id ? (
                      <input
                        type="text"
                        value={editingData.manager || ''}
                        onChange={(e) => handleEditingDataChange('manager', e.target.value)}
                        className="edit-input"
                      />
                    ) : (
                      advertiser.manager
                    )}
                  </td>
                  <td className="memo-col">
                    {editingId === advertiser.id ? (
                      <textarea
                        value={editingData.memo || ''}
                        onChange={(e) => handleEditingDataChange('memo', e.target.value)}
                        className="edit-textarea"
                        rows="2"
                        placeholder="메모를 입력하세요"
                      />
                    ) : (
                      <div className="memo-display" title={advertiser.memo} onClick={() => handleViewMemoModalOpen(advertiser.memo)}>
                        {advertiser.memo && advertiser.memo !== '-' ? advertiser.memo : '-'}
                      </div>
                    )}
                  </td>
                  <td className="date-col">{advertiser.createdAt}</td>
                  <td>
                    <button 
                      className={`action-btn use-alim-btn ${advertiser.useAlimtalk === "N" ? "cRed" : ""}`}
                      onClick={() => handleUseAlimtalk(advertiser.id)}
                      title="알림톡 허용 여부"
                      disabled={editingId === advertiser.id}
                    >
                      {advertiser.useAlimtalk && advertiser.useAlimtalk === "Y" ? 
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#fff" viewBox="0 0 640 640"><path d="M320 160C231.6 160 160 231.6 160 320C160 408.4 231.6 480 320 480C408.4 480 480 408.4 480 320C480 231.6 408.4 160 320 160zM544 320C544 443.7 443.7 544 320 544C196.3 544 96 443.7 96 320C96 196.3 196.3 96 320 96C443.7 96 544 196.3 544 320z"/></svg>
                      :<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#fff" viewBox="0 0 640 640"><path d="M504.6 148.5C515.9 134.9 514.1 114.7 500.5 103.4C486.9 92.1 466.7 93.9 455.4 107.5L320 270L184.6 107.5C173.3 93.9 153.1 92.1 139.5 103.4C125.9 114.7 124.1 134.9 135.4 148.5L278.3 320L135.4 491.5C124.1 505.1 125.9 525.3 139.5 536.6C153.1 547.9 173.3 546.1 184.6 532.5L320 370L455.4 532.5C466.7 546.1 486.9 547.9 500.5 536.6C514.1 525.3 515.9 505.1 504.6 491.5L361.7 320L504.6 148.5z"/></svg>}
                    </button>
                  </td>
                  <td>
                    <button 
                      className="action-btn add-btn"
                      onClick={() => handleAddClick(advertiser.id)}
                      title="추가"
                      disabled={editingId === advertiser.id}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14"/>
                        <path d="M5 12h14"/>
                      </svg>
                    </button>
                  </td>
                  <td>
                    <button 
                      className="action-btn view-btn"
                      onClick={() => handleViewContents(advertiser.id)}
                      title="보기"
                      disabled={editingId === advertiser.id}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"/>
                        <path d="M21 21l-4.35-4.35"/>
                      </svg>
                    </button>
                  </td>
                  <td>
                    {editingId === advertiser.id ? (
                      <div className="edit-actions">
                        <button 
                          className="action-btn save-btn"
                          onClick={() => handleEdit(advertiser.id)}
                          title="저장"
                        >
                          수정
                        </button>
                        <button 
                          className="action-btn cancel-btn"
                          onClick={handleCancelEdit}
                          title="취소"
                        >
                          취소
                        </button>
                      </div>
                    ) : (
                      <button 
                        className="action-btn edit-btn"
                        onClick={() => handleEdit(advertiser.id)}
                        title="편집"
                        disabled={editingId !== null && editingId !== advertiser.id}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                        </svg>
                      </button>
                    )}
                  </td>
                  <td>
                    <button 
                      className="action-btn delete-btn"
                      onClick={() => handleDelete(advertiser.id)}
                      title="삭제"
                      disabled={editingId === advertiser.id}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18"/>
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        <div className="pagination">
          {renderPagination()}
        </div>
      </div>

      {/* 추가 모달 */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>콘텐츠 추가</h3>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="modalType">타입</label>
                <select
                  id="modalType"
                  value={modalForm.type}
                  onChange={(e) => handleModalFormChange('type', e.target.value)}
                  className="modal-select"
                >
                  <option value="" disabled>타입을 선택하세요</option>
                  {getModalTypeOptions().map(option => (
                    <option key={option.idx} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="modalLink">링크</label>
                <input
                  type="url"
                  id="modalLink"
                  value={modalForm.link}
                  onChange={(e) => handleModalFormChange('link', e.target.value)}
                  placeholder="https://example.com"
                  className="modal-input"
                />
              </div>

              {/* 이미지 등록 필드 - 블로그 리뷰, 홍보영상 제외 */}
              {!isImageNotRequired(modalForm.type) && (
                <div className="form-group">
                  <label htmlFor="modalImage">이미지 등록</label>
                  <input
                    type="file"
                    id="modalImage"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="modal-file-input"
                  />
                  {modalForm.image && modalForm.imagePreview && (
                    <div className="image-preview">
                      <img 
                        src={modalForm.imagePreview} 
                        alt="미리보기" 
                        className="preview-image"
                      />
                      <div className="file-info">
                        {modalForm.image.name} ({(modalForm.image.size / 1024 / 1024).toFixed(2)} MB)
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* 이미지가 필요하지 않은 타입에 대한 안내 메시지 */}
              {isImageNotRequired(modalForm.type) && (
                <div className="form-group">
                  <div className="info-message">
                    <span className="info-icon">ℹ️</span>
                    <span>"{getModalTypeOptions().find(opt => opt.id === modalForm.type)?.name}" 타입은 이미지 등록이 필요하지 않습니다.</span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button
                type="button"
                onClick={handleModalClose}
                className="modal-btn cancel-btn"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleModalSubmit}
                disabled={isModalSubmitting}
                className="modal-btn submit-btn"
              >
                {isModalSubmitting ? '추가 중...' : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 컨텐츠 보기 팝업 */}
      {isContentPopupOpen && (
        <div className="modal-overlay" onClick={handleCloseContentPopup}>
          <div className="content-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>컨텐츠 목록</h3>
              <button 
                className="close-btn"
                onClick={handleCloseContentPopup}
                title="닫기"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18"/>
                  <path d="M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            <div className="popup-body">
              {isLoadingContents ? (
                <div className="loading-message">
                  컨텐츠를 불러오는 중...
                </div>
              ) : (
                <div className="content-table-body-container">
                  <div className="content-table-search-type-box">
                    <div> 
                      <div className="content-table-search-type-box-left-item">
                        <span>타입</span>
                        <select
                  id="content-search-type"
                  value={contentModalType}
                  onChange={(e) => handleViewInnerContents(selectedCustomerIdx, e.target.value)}
                  className="modal-select"
                >
                  <option value="all">전체</option>
                  {getModalTypeOptions().map(option => (
                    <option key={option.idx} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
                      </div>
                    </div>
                    <div className="content-table-search-type-box-right"></div>
                  </div>
                  <div className="content-table-wrapper">
                  {/* 선택삭제 버튼 */}
                  <div className="content-controls">
                    <span className="selected-count">
                      {selectedContentItems.length}개 선택됨
                    </span>
                    <div className="content-controls-box">
                    {contentModalType != "all" && <button 
                      className="send-alimtalk-btn"
                      onClick={handleSendContentAlimtalk}
                    >
                      알림톡 발송
                    </button>}
                    <button 
                      className="excel-download-btn"
                      onClick={handleExcelDownload}
                    >
                      엑셀 다운로드
                    </button>
                    <button 
                      className="delete-selected-btn"
                      onClick={handleDeleteSelectedContents}
                      disabled={selectedContentItems.length === 0}
                    >
                      선택 삭제
                    </button>
                    </div>
                  </div>
                  
                  <div className="content-table-container">
                    <table className="content-table">
                      <thead>
                        <tr>
                          <th className="checkbox-col">
                            <input
                              type="checkbox"
                              checked={selectAllContents}
                              onChange={(e) => handleSelectAllContents(e.target.checked)}
                            />
                          </th>
                          <th>타입</th>
                          <th>링크</th>
                          <th>미리보기</th>
                          <th>삭제</th>
                        </tr>
                      </thead>
                    <tbody>
                      {customerContents.length > 0 ? (
                        customerContents.map((content, index) => (
                          <tr key={index}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedContentItems.includes(index)}
                                onChange={(e) => handleSelectContentItem(index, e.target.checked)}
                              />
                            </td>
                            <td className="type-col">{getModalTypeOptions().find(x => x.id == content.type)?.name || '-'}</td>
                            <td className="link-col">
                              {content.url ? (
                                <a 
                                  href={content.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="content-link"
                                >
                                  {content.url}
                                </a>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="preview-col">
                              {content.imageUrl ? (
                                <img 
                                  src={content.imageUrl} 
                                  alt="미리보기" 
                                  className="preview-thumbnail"
                                  onClick={() => {
                                    const fileName = content.imageUrl.split("/").pop() || "image.png";
        const link = document.createElement("a");
        link.href = `https://api.h-ad-report.com/Customer/ImageDownload?url=${encodeURIComponent(content.imageUrl)}&fileName=${fileName}`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
                                  }}
                                />
                              ) : (
                                content.fileData ? (
                                  <img 
                                    src={`data:image/*;base64,${content.fileData}`} 
                                    alt="미리보기" 
                                    className="preview-thumbnail"
                                  />
                                ) : content.contentType === "image" ? (
                                  <button 
                                className="action-btn"
                                onClick={() => handleAddImageModalClick(content.idx, content.type, content.contentType)}
                                title="추가"
                              >+</button>
                                ) : (
                                  '-'
                                )
                              )}
                            </td>
                            <td className="action-col">
                              <button 
                                className="action-btn delete-btn"
                                onClick={() => handleDeleteContent(index)}
                                title="삭제"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M3 6h18"/>
                                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
                                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                                </svg>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="no-content">
                            등록된 컨텐츠가 없습니다.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  </div>
                </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 일괄 업데이트 모달 */}
      {isBatchUpdateModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>콘텐츠 일괄 업데이트</h3>
            </div>
            
            <div className="modal-body">
            <div className="form-group">
                  <label htmlFor="modalImage">엑셀파일 등록</label>
                  <input
                    type="file"
                    id="modalExcelFile"
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handleBatchUpdateFormChange}
                    className="modal-file-input"
                  />
                  {batchUpdateForm.excelFile && batchUpdateForm.excelFile.name && (
                    <div className="excel-file-preview">
                      <div className="file-info">
                        {batchUpdateForm.excelFile.name} ({(batchUpdateForm.excelFile.size / 1024 / 1024).toFixed(2)} MB)
                      </div>
                    </div>
                  )}
                </div>
            </div>
            
            <div className="modal-footer">
              <button
                type="button"
                onClick={handleBatchUpdateModalClose}
                className="modal-btn cancel-btn"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleBatchUpdateSubmit}
                disabled={isBatchUpdateSubmitting}
                className="modal-btn submit-btn"
              >
                {isBatchUpdateSubmitting ? '추가 중...' : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 일괄 업데이트 로그 모달 */}
      {isBatchLogModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>콘텐츠 일괄 업데이트 로그 조회</h3>
            </div>
            
            <div className="modal-body">
            <div className="form-group">
                  <label htmlFor="modalSearchDate">조회 날짜 설정</label>
                  <input
                    type="date"
                    id="modalSearchDate"
                    value={batchLogForm.searchDate}
                    onChange={(e) => setBatchLogForm({
                      searchDate:  e.target.value
                    })}
                  />
                </div>
            </div>
            
            <div className="modal-footer">
              <button
                type="button"
                onClick={handleBatchLogModalClose}
                className="modal-btn cancel-btn"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleBatchLogSubmit}
                disabled={isBatchLogSubmitting}
                className="modal-btn submit-btn"
              >
                {isBatchLogSubmitting ? '다운로드 중...' : '다운로드'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 작업현황 다운로드 모달 */}
      {isWorkStatusDownloadModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>작업현황 다운로드</h3>
            </div>
            
            <div className="modal-body">
            <div className="form-group">
                  <label htmlFor="workStatusDownloadSearchDate">조회 날짜 설정</label>
                  <input
                    type="month"
                    id="workStatusDownloadSearchDate"
                    value={workStatusDownloadForm.searchDate}
                    onChange={(e) => setWorkStatusDownloadForm({
                      searchDate:  e.target.value
                    })}
                  />
                </div>
            </div>
            
            <div className="modal-footer">
              <button
                type="button"
                onClick={handleWorkStatusDownloadModalClose}
                className="modal-btn cancel-btn"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleWorkStatusDownloadSubmit}
                disabled={isWorkStatusDownloadSubmitting}
                className="modal-btn submit-btn"
              >
                {isWorkStatusDownloadSubmitting ? '다운로드 중...' : '다운로드'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 메모 보기 모달 */}
      {isViewMemoModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>메모</h3>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                  {selectViewMemo && (
                    <textarea
                    value={selectViewMemo}
                    readOnly
                    rows={10}
                    style={{resize:"none"}}
                  />
                  )}
              </div>
            </div>
            
            <div className="modal-footer">
              <button
                type="button"
                onClick={handleViewMemoModalClose}
                className="modal-btn cancel-btn"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 컨텐츠 이미지 추가 모달 */}
      {isAddImageModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>이미지 등록</h3>
            </div>
            
            <div className="modal-body">
               <div className="form-group">
                  <label htmlFor="addImageModal">이미지 등록</label>
                  <input
                    type="file"
                    id="addImageModal"
                    accept="image/*"
                    onChange={handleImageUpload2}
                    className="modal-file-input"
                  />
                  {addImageModalForm.image && addImageModalForm.imagePreview && (
                    <div className="image-preview">
                      <img 
                        src={addImageModalForm.imagePreview} 
                        alt="미리보기" 
                        className="preview-image"
                      />
                      <div className="file-info">
                        {addImageModalForm.image.name} ({(addImageModalForm.image.size / 1024 / 1024).toFixed(2)} MB)
                      </div>
                    </div>
                  )}
                </div>

            </div>
            
            <div className="modal-footer">
              <button
                type="button"
                onClick={handleAddImageModalClose}
                className="modal-btn cancel-btn"
              >
                닫기
              </button>
              <button
                type="button"
                onClick={handleImageUploadSubmit}
                disabled={isAddImageUploadSubmitting}
                className="modal-btn submit-btn"
              >
                {isAddImageUploadSubmitting ? '추가 중...' : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default ManageUser; 